import { FileCheck, Zap, Cable, Lightbulb, Plug, Wrench, CheckCircle, Clock, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { services } from '../mockData';

const iconMap = {
  FileCheck,
  Zap,
  Cable,
  Lightbulb,
  Plug,
  Wrench
};

export const Services = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-700 text-white py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Our Services</h1>
          <p className="text-xl text-blue-100 max-w-2xl">
            Professional electrical services for landlords, letting agents, and property managers across Greater London
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {services.map((service, index) => {
              const Icon = iconMap[service.icon];
              const isEven = index % 2 === 0;
              
              return (
                <Card key={service.id} className="overflow-hidden border-gray-200">
                  <div className={`grid lg:grid-cols-2 gap-0 ${isEven ? '' : 'lg:flex-row-reverse'}`}>
                    <div className={`p-8 lg:p-12 ${isEven ? '' : 'lg:order-2'}`}>
                      <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                        <Icon className="h-8 w-8 text-blue-600" />
                      </div>
                      
                      <h2 className="text-3xl font-bold text-gray-900 mb-4">{service.title}</h2>
                      <p className="text-lg text-gray-600 mb-6">{service.fullDesc}</p>
                      
                      <div className="mb-6">
                        <h3 className="font-semibold text-gray-900 mb-3">What's included:</h3>
                        <ul className="space-y-2">
                          {service.includes.map((item, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-600">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4 mb-6">
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <p className="text-sm font-semibold text-gray-700 mb-1">Perfect for:</p>
                          <p className="text-gray-600">{service.whoFor}</p>
                        </div>
                        <div className="bg-blue-50 p-4 rounded-lg">
                          <div className="flex items-center gap-2 mb-1">
                            <Clock className="h-4 w-4 text-blue-600" />
                            <p className="text-sm font-semibold text-gray-700">Turnaround:</p>
                          </div>
                          <p className="text-gray-600">{service.turnaround}</p>
                        </div>
                      </div>

                      <Link to="/contact">
                        <Button className="gap-2 bg-blue-600 hover:bg-blue-700">
                          Get a Quote
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </Link>
                    </div>

                    <div className={`bg-gray-200 min-h-[300px] lg:min-h-full ${isEven ? '' : 'lg:order-1'}`}>
                      <img
                        src={
                          index === 0
                            ? "https://images.unsplash.com/photo-1758101755915-462eddc23f57?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBlbGVjdHJpY2lhbiUyMGVsZWN0cmljYWwlMjBpbnNwZWN0aW9uJTIwdGVzdGluZ3xlbnwwfHx8fDE3ODY2MzM4ODh8MA&ixlib=rb-4.1.0&q=85"
                            : index === 1
                            ? "https://images.unsplash.com/photo-1635335874521-7987db781153?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxuZXclMjBtb2Rlcm4lMjBjb25zdW1lciUyMHVuaXQlMjBmdXNlYm94JTIwUkNCTyUyMHN1cmdlJTIwcHJvdGVjdGlvbnxlbnwwfHx8fDE3ODY2MzQ0NTh8MA&ixlib=rb-4.1.0&q=85"
                            : index === 2
                            ? "https://images.unsplash.com/photo-1775714351784-51e93e4c12a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHw0fHxlbGVjdHJpY2FsJTIwY2FibGUlMjBkcnVtJTIwdHdpbiUyMGVhcnRoJTIwd2lyaW5nJTIwc3Bvb2xzfGVufDB8fHx8MTc4NjYzNDQ1OHww&ixlib=rb-4.1.0&q=85"
                            : index === 3
                            ? "https://images.unsplash.com/photo-1561715608-5659baeccfb4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxlbWVyZ2VuY3klMjBsaWdodGluZyUyMGluc3RhbGxhdGlvbiUyMGNvbW1lcmNpYWx8ZW58MHx8fHwxNzg2NjMzOTAyfDA&ixlib=rb-4.1.0&q=85"
                            : "https://images.unsplash.com/photo-1725550364000-a0df5b5abac4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwzfHxkYW1hZ2VkJTIwY2hld2VkJTIwZWxlY3RyaWNhbCUyMGNhYmxlJTIwd2lyZSUyMHJvZGVudHxlbnwwfHx8fDE3ODY2MzQwNjR8MA&ixlib=rb-4.1.0&q=85"
                        }
                        alt={service.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Contact us today for a free quote or to discuss your electrical requirements
          </p>
          <Link to="/contact">
            <Button size="lg" className="gap-2 bg-blue-600 hover:bg-blue-700">
              Contact Us Today
              <ArrowRight className="h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
